import { useEffect, useState } from 'react';

import {
    PutStateRequestBody,
    fetchGetStateByStateKey,
    fetchPutState
} from '@iwoca/lapi-client/edge';
import { CheckBox } from '@iwoca/orion';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { produce } from 'immer';
import { FormattedMessage } from 'react-intl';

import messages from './PennyTransferModal.messages';
import styles from './PennyTransferModal.module.css';
import * as IwDataDogLogger from '../../../../../iwLogger/IwDataDogLogger';
import { useSiteContext } from '../../../../../iwQueryClient/useSiteContext';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../../../../modals/fallbackErrorModal/FallbackErrorModal';
import MainContentPending from '../../../../pageStructure/mainContentPending';

export const PennyTransferModal = () => {
    const [pennyTransferSent, setPennyTransferSent] = useState(false);
    const [isCheckboxEnabled, setIsCheckboxEnabled] = useState(true);
    const [pennyTransferSentPending, setPennyTransferSentPending] =
        useState(false);

    const stateKey = useStateKey();

    const siteContext = useSiteContext();

    const {
        data: customerStateResponse,
        isLoading: customerStateLoading,
        isError: customerStateError
    } = useQuery({
        queryKey: ['customer_state', stateKey],
        queryFn: () => fetchGetStateByStateKey({ stateKey })
    });

    const queryClient = useQueryClient();
    const saveCustomerState = useMutation({
        mutationFn: (state: PutStateRequestBody) =>
            fetchPutState({
                stateKey: stateKey,
                body: state
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['customer_state', stateKey]
            });
        }
    });

    useEffect(() => {
        if (customerStateResponse) {
            const isPennyTransferSent =
                //@ts-expect-error: UI does not exist on Customer State Response LAPI client type
                customerStateResponse.data.ui?.draw_down?.penny_transfer_sent;

            if (isPennyTransferSent) {
                setIsCheckboxEnabled(false);
                setPennyTransferSent(true);
            }
        }
    }, [customerStateResponse]);

    if (customerStateLoading) {
        return <MainContentPending />;
    }
    if (customerStateError) {
        return <FallbackErrorModal />;
    }

    const pennyTransferAccountNumber =
        siteContext.data?.user_repayment_details?.account_number;
    const pennyTransferSortCode =
        siteContext.data?.user_repayment_details?.sort_code;
    const pennyTransferAccountName =
        siteContext.data?.user_repayment_details?.owner_name;

    const handlePennyTransferSent = async () => {
        setPennyTransferSent(true);
        setPennyTransferSentPending(true);
        try {
            const pennyTransferSentDate = new Date().toISOString();

            const newCustomerState = produce(customerStateResponse, (draft) => {
                //@ts-expect-error: ui does not exist on type definition
                draft.data.ui = draft.data.ui || {};
                //@ts-expect-error: ui does not exist on type definition
                draft.data.ui.draw_down = draft.data.ui.draw_down || {};
                //@ts-expect-error: ui does not exist on type definition
                draft.data.ui.draw_down.penny_transfer_sent =
                    pennyTransferSentDate;
            });
            await saveCustomerState.mutateAsync(newCustomerState);

            setIsCheckboxEnabled(false);
            setPennyTransferSentPending(false);
        } catch (error: any) {
            IwDataDogLogger.logError(
                error.message,
                {
                    tags: { reporter: 'handle_penny_transfer_sent' }
                },
                error
            );
        }
    };

    return (
        <div className={styles.PennyTransferModal}>
            <div className={styles.header}>
                <FormattedMessage {...messages.title}>
                    {(...msg) => <h1 className={styles.title}>{msg}</h1>}
                </FormattedMessage>
                <FormattedMessage tagName="div" {...messages.subTitle} />
            </div>
            {/* transfer Details */}
            <section
                className={styles.transferDetails}
                data-testid="transfer-details"
            >
                <div>
                    <FormattedMessage
                        tagName="strong"
                        {...messages.accountNumber}
                    />
                    <p
                        className={classNames(
                            styles.transferInfo,
                            'fs-exclude'
                        )}
                    >
                        {pennyTransferAccountNumber}
                    </p>
                </div>
                <div>
                    <FormattedMessage tagName="strong" {...messages.sortCode} />
                    <p
                        className={classNames(
                            styles.transferInfo,
                            'fs-exclude'
                        )}
                    >
                        {pennyTransferSortCode}
                    </p>
                </div>
                <div>
                    <FormattedMessage
                        tagName="strong"
                        {...messages.accountName}
                    />
                    <p
                        className={classNames(
                            styles.transferInfo,
                            'fs-exclude'
                        )}
                    >
                        {pennyTransferAccountName}
                    </p>
                </div>
            </section>
            {/* transfer Details end*/}
            <CheckBox
                label={messages.sentPenny.defaultMessage}
                onChange={() => handlePennyTransferSent()}
                checked={pennyTransferSent}
                disabled={!isCheckboxEnabled}
            />
            {!isCheckboxEnabled && (
                <div className={styles.verifyingSubTitle}>
                    <FormattedMessage {...messages.verifyingSubTitle} />
                </div>
            )}
            {pennyTransferSentPending && (
                <div className={styles.verifyingSubTitle}>
                    <FormattedMessage {...messages.verifyingTitle} />
                </div>
            )}
        </div>
    );
};
